/**
 * The PIN screen shown on cold start and after the background timeout.
 *
 * Offers biometrics first when the running build supports them, falling back to
 * the PIN on cancel or failure. There is deliberately no "forgot PIN" self-serve
 * path: the PIN only exists on this device, so recovery means an admin reset
 * from the nurse profile. Logging out is offered instead, which clears the lock
 * and lets her sign in fresh.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { PinPad } from "@/components/app-lock/PinPad";
import {
    authenticateWithBiometrics,
    clearPinAttempts,
    getStoredMethod,
    isBiometricAvailable,
    loadPinAttempts,
    PIN_LENGTH,
    savePinAttempts,
    verifyPin
} from "@/lib/app-lock";
import {
    mergeAttempts,
    NO_PIN_ATTEMPTS,
    PIN_DELAY_MS,
    recordWrongPin,
    remainingDelayMs,
    type PinAttemptState
} from "@/lib/app-lock-attempts";
import { useAppLock } from "@/lib/app-lock-context";
import { useAuth } from "@/lib/auth";
import { colors, fonts } from "@/lib/theme";

// Slows down someone tapping through the keyspace on a found phone: 5 wrong
// PINs, then a 30-second pause (lib/app-lock-attempts.ts). Not a lockout: a
// nurse who genuinely forgot her PIN must still be able to reach the logout
// button and sign back in. The count is saved on the device, so killing the
// app no longer resets it.

export function LockScreen() {
    const { markUnlocked } = useAppLock();
    const { logout } = useAuth();

    const [pin, setPin] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [checking, setChecking] = useState(false);
    const [biometricOffered, setBiometricOffered] = useState(false);
    const attempts = useRef<PinAttemptState>({ ...NO_PIN_ATTEMPTS });

    // Pick up the count saved before the app was last closed. Anything counted
    // in the moments before this resolves is kept (mergeAttempts never lowers
    // a count or shortens a pause).
    useEffect(() => {
        let cancelled = false;
        void loadPinAttempts().then((saved) => {
            if (!cancelled) {
                attempts.current = mergeAttempts(saved, attempts.current, Date.now());
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const unlock = useCallback(() => {
        // Any successful unlock starts the next lock from zero, as a fresh lock
        // screen always did.
        attempts.current = { ...NO_PIN_ATTEMPTS };
        void clearPinAttempts();
        markUnlocked();
    }, [markUnlocked]);

    const tryBiometric = useCallback(async () => {
        const ok = await authenticateWithBiometrics();
        if (ok) {
            unlock();
        }
    }, [unlock]);

    // Offer biometrics once on mount when this build and this nurse both have
    // them. If it is unavailable the screen is simply a PIN prompt.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [available, method] = await Promise.all([
                isBiometricAvailable(),
                getStoredMethod()
            ]);
            if (cancelled || !available || method !== "biometric") {
                return;
            }
            setBiometricOffered(true);
            await tryBiometric();
        })();
        return () => {
            cancelled = true;
        };
    }, [tryBiometric]);

    const onChange = useCallback(
        async (next: string) => {
            setError(null);
            setPin(next);
            if (next.length < PIN_LENGTH) {
                return;
            }

            const waitMs = remainingDelayMs(attempts.current, Date.now());
            if (waitMs > 0) {
                const secs = Math.ceil(waitMs / 1000);
                setError(`Too many attempts. Try again in ${secs}s.`);
                setPin("");
                return;
            }

            setChecking(true);
            const ok = await verifyPin(next);
            setChecking(false);

            if (ok) {
                unlock();
                return;
            }

            const updated = recordWrongPin(attempts.current, Date.now());
            attempts.current = updated;
            void savePinAttempts(updated);
            setPin("");
            if (updated.blockedUntil > 0) {
                setError(`Too many attempts. Try again in ${PIN_DELAY_MS / 1000}s.`);
            } else {
                setError("Incorrect PIN. Try again.");
            }
        },
        [unlock]
    );

    return (
        <SafeAreaView style={styles.safe}>
            <PinPad
                title="Enter your PIN"
                subtitle="Your PIN protects patient information on this device."
                value={pin}
                length={PIN_LENGTH}
                error={error}
                disabled={checking}
                onChange={(v) => void onChange(v)}
                footer={
                    <View style={styles.footer}>
                        {checking ? <ActivityIndicator color={colors.teal400} /> : null}
                        {biometricOffered ? (
                            <TouchableOpacity onPress={() => void tryBiometric()}>
                                <Text style={styles.link}>Use biometric unlock</Text>
                            </TouchableOpacity>
                        ) : null}
                        <TouchableOpacity onPress={() => void logout()}>
                            <Text style={styles.logout}>Log out</Text>
                        </TouchableOpacity>
                    </View>
                }
            />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.gray50 },
    footer: { alignItems: "center", gap: 18 },
    link: { fontSize: 15, fontFamily: fonts.semiBold, color: colors.teal600 },
    logout: { fontSize: 14, fontFamily: fonts.medium, color: colors.gray500 }
});
